import PropTypes from "prop-types";
import { useState } from "react";
import { Link } from "react-router-dom";
import clsx from "clsx";

// Helpers
import { dateDifference } from "@/Common/helpers/Dates";
import { getFormattedDate } from "@dealerwave/dw-layer-date-converter";

// Hooks
import { useLocation } from "@Common/hooks/Location/useLocation";
import { useAuthorization } from "@/Common/hooks/Authorization/Authorization";

// MUI
import { Box, ButtonBase, Dialog, Grid, IconButton, Menu, MenuItem, Paper, Tooltip, Typography } from "@mui/material";

// Permission
import actions from "@/Constants/actionsList";

// Components
import AlertWithIcon from "@/Layouts/components/MessagesAlert/AlertWithIcon";
import DateDiff from "@/Layouts/components/DateDiff";
import PdfPayPlanLayer from "@/PayPlans/views/Layers/Html/Sales/Associates";
import GeneralDialog from "@/Layouts/components/Dialogs/GeneralDialog";

//Images
import empty from "@/assets/images/commons/empty.png";
import EmptyBody from "@Layouts/components/Basics/EmptyBody";

// Assets
import { MoreHoriz } from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import { ReactComponent as SystemSettingsIcon } from "@/assets/icons/header/settings/SystemSettingsIcon.svg";
import { ReactComponent as SignatureIcon } from "@/assets/icons/svg/signature.svg";
import { ReactComponent as PencilEdition } from "@/assets/icons/svg/pencil_edition.svg";
import { ReactComponent as Draft } from "@PayPlans/assets/icons/oneSpanStatus/Draft.svg";

// Constants
import { DATE_FORMAT } from "@/Constants/date";

export default function PayPlanInformationCard(props) {
	const { useAuth } = useAuthorization();

	const { timezone } = useLocation();

	const VIEW_SALES_ASSOCIATE_PAY_PLANS = useAuth(actions.VIEW_SALES_ASSOCIATE_PAY_PLANS);
	const VIEW_OTHER_USER_PROFILES = useAuth(actions.VIEW_OTHER_USER_PROFILES);
	const EDIT_SALES_ASSOCIATE_PAY_PLANS = useAuth(actions.EDIT_SALES_ASSOCIATE_PAY_PLANS);
	const { currentPayPlanAssignment, upcomingPayPlanAssignment, sectionType } = props;
	//User State and Menu User
	const [menuVisible, setMenuVisible] = useState(false);
	const [userAnchorEl, setUserAnchorEl] = useState();

	const handleCloseMenu = () => {
		setMenuVisible(false); // Hide the menu
	};
	//Menu Options
	const [anchorEl, setAnchorEl] = useState();
	const open = Boolean(anchorEl);

	const handleClick = (event) => {
		setAnchorEl(event.currentTarget);
	};

	const handleClose = () => {
		setAnchorEl(null);
	};

	//Iframe One Span Modal
	const [showIframe, setShowIframe] = useState(false);
	const showEmbeddedComponent = () => {
		handleClose();
		setShowIframe(!showIframe);
	};

	const handleCloseDialog = () => {
		setShowIframe(false);
	};

	//Show PDF Modal
	const [selected, setSelected] = useState(null);
	const [pdfVisible, setPdfVisible] = useState(false);
	const DAYS_THRESHOLD = 7;

	const effectiveDateWithin = () => {
		const diff = Math.abs(dateDifference(new Date(upcomingPayPlanAssignment?.effectiveDate)));
		if (!diff) {
			return false;
		}
		return diff <= DAYS_THRESHOLD;
	};

	const handleOpenPayPlanModel = (_event, payplanSelected) => {
		setSelected(payplanSelected);
		setPdfVisible((prev) => !prev);
		handleClose();
	};

	const supervisorUrl = upcomingPayPlanAssignment?.embeddedSigningLinks?.find((element) => element?.role === "supervisor")?.url;
	const acknowledgementStatus = getAcknowledgeMentStatus(upcomingPayPlanAssignment);
	const lastAcknowledgementStatus = acknowledgementStatus?.[acknowledgementStatus?.length - 1];
	const lastStatus = lastAcknowledgementStatus?.status;
	const signingStatusCompleted = lastStatus === "COMPLETED";
	const signingStatusDraft = lastStatus === "DRAFT";
	const signingProgress = getSigningProgress(lastAcknowledgementStatus);
	const assigneeInfo = signingProgress.find((element) => element?.roleId === "assignee");
	const supervisorInfo = signingProgress.find((element) => element?.roleId === "supervisor");

	const buttonContent = getButtonContent(signingStatusCompleted, signingStatusDraft, supervisorInfo);

	const effectiveDate = upcomingPayPlanAssignment?.effectiveDate;
	const acknowledgementDate = upcomingPayPlanAssignment?.acknowledgementDate;
	const styleColorRed = "var(--text-alert-color) !important";
	const styleColorText = "var(--text-color) !important";

	const content = getContent({
		effectiveDate,
		acknowledgementDate,
		styleColorText,
		upcomingPayPlanAssignment,
		effectiveDateWithin,
		sectionType,
		timezone,
	});

	const menuUsers = (
		<Menu
			id="basic-menu-user"
			anchorEl={userAnchorEl}
			open={menuVisible}
			onClose={handleCloseMenu}
			MenuListProps={{
				"aria-labelledby": "basic-button",
			}}
			className="min-w-[210px] !py-0"
			sx={{
				"& .MuiMenu-paper": {
					borderRadius: "12px",
				},
			}}
		>
			{getMenuItemsUsers(assigneeInfo, supervisorInfo)}
		</Menu>
	);

	const dialogContent = (
		<Dialog maxWidth="sm" open={pdfVisible} disableEscapeKeyDown onClose={() => setPdfVisible(false)} className="select-plan-dialog">
			<Paper
				elevation={0}
				sx={{
					padding: "35px 0px",
					height: "100%",
					borderRadius: "20px",
				}}
			>
				<Grid container className={"add-vertical-scroll-behavior"} sx={{ padding: "0 30px" }}>
					<IconButton
						onClick={() => setPdfVisible(false)}
						aria-label="close"
						sx={{
							position: "absolute",
							top: "10px",
							right: "10px",
						}}
					>
						<CloseIcon />
					</IconButton>
					<PdfPayPlanLayer assignment={true} selected={selected} payPlan={selected} isMyPayPlan={true} />
				</Grid>
			</Paper>
		</Dialog>
	);

	return (
		<Paper elevation={0} className="!shadow-card-gray-light !rounded-[10px] p-1">
			{showIframe && (
				<GeneralDialog onClose={handleCloseDialog} open={showIframe}>
					{!supervisorUrl ? (
						<EmptyBody image={empty} title="No Pay Plan to Sign" />
					) : (
						<iframe src={supervisorUrl} title="Embedded Pay Plan" className={"embedded-iframe"} />
					)}
				</GeneralDialog>
			)}
			{pdfVisible && dialogContent}

			<Grid container position="relative" sx={{ padding: "5px 5px 5px 5px" }}>
				{upcomingPayPlanAssignment ? (
					<div className="!absolute !right-2 !top-[-2px] flex items-center gap-2">
						{buttonContent}
						<button
							id="basic-default"
							aria-controls={getAriaControls(open)}
							aria-haspopup="true"
							aria-expanded={getAriaExpanded(open)}
							onClick={handleClick}
						>
							<MoreHoriz sx={{ color: "var(--text-color)" }} />
						</button>
						<Menu
							id="basic-menu"
							anchorEl={anchorEl}
							open={open}
							onClose={handleClose}
							MenuListProps={{
								"aria-labelledby": "basic-button",
							}}
							sx={{
								"& .MuiMenu-paper": {
									borderRadius: "12px",
								},
							}}
						>
							<MenuItem>
								{EDIT_SALES_ASSOCIATE_PAY_PLANS && (
									<Link
										to={{
											pathname: "/pay-plans/individual-assignments",
											search: `?selected=${props?.position}&selected=${props?._id}&selected=${props?.fullName}`,
										}}
									>
										<div className="flex gap-2">
											<SystemSettingsIcon width={25} color="var(--text-color)" />
											<span className="font-montserrat text-gray-text text-[12px]">Edit Upcoming</span>
										</div>
									</Link>
								)}
							</MenuItem>
							<MenuItem onClick={showEmbeddedComponent} className="flex gap-2">
								<SignatureIcon width={25} color="var(--text-color)" />
								<span className="font-montserrat text-gray-text text-[12px]">Sign Upcoming</span>
							</MenuItem>
						</Menu>
					</div>
				) : null}

				<Typography
					variant="div"
					display="block"
					className={clsx("!text-[12px]", "text-gray-text font-medium", {
						"forbidden-access": !VIEW_OTHER_USER_PROFILES,
					})}
				>
					{props?.fullName}
				</Typography>

				<Grid
					xs={12}
					sx={{
						display: "flex",
						alignItems: "center",
						columnGap: 1,
					}}
					item
				>
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							textDecoration: "underline",
							marginTop: "2px",
						}}
					>
						<Typography
							sx={{
								fontWeight: "bold",
								display: "flex",
								alignItems: "center",
							}}
							className="group-pay-plan-assign-container-select-all !text-[var(--primary-color)]"
						>
							<span className={`${upcomingPayPlanAssignment ? "" : "text-disabled decoration-disabled underline"}`}>
								Upcoming:&nbsp;
							</span>
							{upcomingPayPlanAssignment ? (
								<ButtonBase
									sx={{
										fontWeight: "bold",
									}}
									disabled={!VIEW_SALES_ASSOCIATE_PAY_PLANS}
									onClick={(event) => handleOpenPayPlanModel(event, props?.upcomingPayPlanAssignment)}
									className="group-pay-plan-assign-container-select-all !text-[var(--primary-color)]"
								>
									{upcomingPayPlanAssignment?.planId?.name}
								</ButtonBase>
							) : (
								<span className={"text-disabled decoration-disabled underline"}>No Upcoming Pay Plan</span>
							)}
						</Typography>
						{menuUsers}
					</Box>

					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							columnGap: 0.5,
						}}
					>
						{content}
					</Box>
				</Grid>

				<Grid
					xs={12}
					sx={{
						display: "flex",
						alignItems: "center",
						columnGap: 1,
					}}
					item
				>
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							columnGap: 0.5,
							textDecoration: "underline",
							marginTop: "2px",
						}}
					>
						<Typography
							sx={{
								fontWeight: "bold",
								display: "flex",
								alignItems: "baseline",
							}}
							className="group-pay-plan-assign-container-select-all"
						>
							<span>Current:&nbsp;</span>
							{currentPayPlanAssignment ? (
								<ButtonBase
									sx={{
										fontWeight: "bold",
									}}
									disabled={!VIEW_SALES_ASSOCIATE_PAY_PLANS}
									onClick={(event) => handleOpenPayPlanModel(event, currentPayPlanAssignment)}
									className="group-pay-plan-assign-container-select-all"
								>
									{props?.currentPayPlanAssignment?.planId?.name}
								</ButtonBase>
							) : null}
						</Typography>
						{menuUsers}
					</Box>

					{currentPayPlanAssignment ? (
						<Box
							sx={{
								display: "flex",
								alignItems: "center",
								columnGap: 0.5,
							}}
						>
							<Typography className="group-pay-plan-assign-container-select-all">Effective:</Typography>
							<Typography className="group-pay-plan-assign-container-select-all w-[140px]">
								{getFormattedDate(currentPayPlanAssignment?.effectiveDate, timezone, DATE_FORMAT.monthAbbrDatePadded)}
							</Typography>
							<DateDiff
								date={props?.currentPayPlanAssignment?.effectiveDate}
								classNameClock="!neutral-gray"
								color="var(--neutral-gray)"
								maxWidth="60px"
							/>
						</Box>
					) : (
						<AlertWithIcon
							className="error-message-span"
							text="No current pay plan assignment."
							icon={<WarningAmberRoundedIcon size="small" />}
							textStyle={{ color: styleColorRed, marginLeft: 0.5 }}
							sx={{
								backgroundColor: "transparent",
							}}
						/>
					)}
				</Grid>
			</Grid>
		</Paper>
	);
}

function getAcknowledgeMentStatus(upcomingPayPlanAssignment) {
	return upcomingPayPlanAssignment?.acknowledgementStatus || [];
}

function getSigningProgress(lastAcknowledgementStatus) {
	return lastAcknowledgementStatus?.signingProgress || [];
}

function getButtonContent(signingStatusCompleted, signingStatusDraft, supervisorInfo) {
	let buttonContent = null;

	if (!signingStatusCompleted) {
		const tooltipText = signingStatusDraft ? "Pay Plan in Draft" : "Requires manager signature";
		const isSupervisorSigned = supervisorInfo?.signedAt;
		let tempContent;
		if (signingStatusDraft) {
			tempContent = <Draft />;
		} else if (!isSupervisorSigned) {
			tempContent = <PencilEdition />;
		} else {
			tempContent = null;
		}

		buttonContent = tempContent ? (
			<Tooltip arrow title={tooltipText}>
				<button className="cursor-default">{tempContent}</button>
			</Tooltip>
		) : null;
	}

	return buttonContent;
}

function getContent(args) {
	const { acknowledgementDate, effectiveDate, effectiveDateWithin, sectionType, styleColorText, timezone, upcomingPayPlanAssignment } =
		args;

	let content;

	if (upcomingPayPlanAssignment) {
		if (!effectiveDate && !!acknowledgementDate && !effectiveDateWithin()) {
			content = (
				<>
					<Typography className="group-pay-plan-assign-container-select-all">Effective</Typography>
					<Typography className="group-pay-plan-assign-container-select-all">
						{getFormattedDate(effectiveDate, timezone, DATE_FORMAT.monthAbbrDatePadded)}
					</Typography>
					<DateDiff date={effectiveDate} classNameClock="!neutral-gray" color="var(--neutral-gray)" />
				</>
			);
		} else {
			content = (
				<AlertWithIcon
					className="error-message-span"
					text={
						effectiveDateWithin() || sectionType === "Acknowledged"
							? "Effective: " + getFormattedDate(effectiveDate, timezone, DATE_FORMAT.monthAbbrDatePadded)
							: "Not yet acknowledged"
					}
					textStyle={{ color: styleColorText, marginLeft: 0.5 }}
					sx={{
						backgroundColor: "transparent",
					}}
				/>
			);
		}
	} else {
		content = null;
	}

	return content;
}

function getAriaControls(open) {
	return open ? "basic-menu" : undefined;
}

function getAriaExpanded(open) {
	return open ? "true" : undefined;
}

function getMenuItemsUsers(assigneeInfo, supervisorInfo) {
	return [
		<MenuItem key="assignee" className="flex gap-2">
			<span className="font-montserrat text-[12px]">{assigneeInfo?.fullName ? assigneeInfo.fullName + ":" : ""}</span>
			<span className="font-montserrat text-[12px] font-medium">{assigneeInfo?.signedAt ? "Signed " : "Not Signed"}</span>
		</MenuItem>,
		<MenuItem key="supervisor" className="flex gap-2">
			<span className="font-montserrat text-[12px]">{supervisorInfo?.fullName ? supervisorInfo.fullName + ":" : ""}</span>
			<span className="font-montserrat text-[12px] font-medium">{supervisorInfo?.signedAt ? "Signed " : "Not Signed"}</span>
		</MenuItem>,
	];
}

PayPlanInformationCard.propTypes = {
	_id: PropTypes.string,
	fullName: PropTypes.string,
	position: PropTypes.string,
	currentPayPlanAssignment: PropTypes.object,
	upcomingPayPlanAssignment: PropTypes.object,
	sectionType: PropTypes.string,
};
